import type { PartnerUser } from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '../GatewayAxiosClient';
import type {
  ChangePasswordRequest,
  Partner,
  UserCreationRequest,
  UpdateVolumeEstimatesParams,
  UpdateReportingConfigParams,
  OptIns,
  PartnerReportingConfig,
  ReturnServiceMethod,
  PasswordConfigurationInfo,
  UserSsoMutate,
  UserFetchParams,
  UserSsoFetchParams,
  PartnerPackagingV2,
  PackagingSetV2,
} from './types';
import type { User, UserSso } from '../AuthService/types';
import { getToken } from '../../session';
import mockPartnerService from './mockPartnerService';
import type { ServerPaginationResponse } from './types';

export class PartnerService {
  public async getPartnerUsers<T extends UserFetchParams | UserSsoFetchParams>(
    partnerId: string,
    params: T = {} as T,
  ): Promise<{ data: User[] } & (T extends UserSsoFetchParams ? ServerPaginationResponse : {})> {
    const res = await GatewayAxiosClient.get(`/partners/${partnerId}/users`, {
      params,
    });

    return {
      data: res.data,
      pageNext: res.headers['x-page-next'],
      pagePrevious: res.headers['x-page-previous'],
      totalCount: Number(res.headers['x-total-count']) || 0,
    };
  }

  public async getPartnerUser(userId: string): Promise<PartnerUser> {
    const [user, sso] = await Promise.allSettled([
      GatewayAxiosClient.get(`/partner-management-proxy/api/v1/users/${userId}`),
      GatewayAxiosClient.get(`/partner-management-proxy/api/v1/users/${userId}/sso`),
    ]);

    if (sso.status === 'fulfilled' && sso.value.data) {
      return sso.value.data;
    }

    if (user.status === 'fulfilled' && user.value.data) {
      return user.value.data;
    }

    throw new Error('User not found!');
  }

  public async getPartnerSsoUsers(partnerId: string, params: UserSsoFetchParams = {}) {
    const res = await GatewayAxiosClient.get(`/partners/${partnerId}/sso/users`, {
      params,
    });
    return res.data;
  }

  public async addSsoUser(newUser: UserSsoMutate): Promise<UserSso> {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/sso/users`, newUser);
    return res.data;
  }

  public async updateSsoUser(userId: string, user: Partial<UserSsoMutate>) {
    const res = await GatewayAxiosClient.patch(`/partner-management-proxy/api/v1/sso/users/${userId}`, user);
    return res.data;
  }

  public async deleteSsoUser(userId: string) {
    const res = await GatewayAxiosClient.delete(`/partner-management-proxy/api/v1/sso/users/${userId}`);
    return res.data;
  }

  public async createPartnerUser(requestParams: UserCreationRequest, accessToken: string): Promise<User> {
    const res = await GatewayAxiosClient.post(`/partner/${requestParams.partnerId}/createUser`, requestParams, {
      headers: {
        authorization: accessToken,
      },
    });
    return res.data;
  }
  public async changePassword(changePasswordRequest: ChangePasswordRequest): Promise<void> {
    const res = await GatewayAxiosClient.post('/partners/updatePassword', {
      ...changePasswordRequest,
    });
    return res.data;
  }
  public async updateVolumeEstimates(args: UpdateVolumeEstimatesParams): Promise<Partner> {
    const payload = {
      daily: {
        average: args.volumeEstimates.daily?.average,
        median: args.volumeEstimates.daily?.average,
        peak: args.volumeEstimates.daily?.peak,
      },
      hourly: {
        average: args.volumeEstimates.daily?.average,
        median: args.volumeEstimates.hourly?.average,
        peak: args.volumeEstimates.hourly?.peak,
      },
      weekly: {
        average: args.volumeEstimates.weekly?.average,
        median: args.volumeEstimates.weekly?.average,
        peak: args.volumeEstimates.weekly?.peak,
      },
    };
    await GatewayAxiosClient.post(`/partners/${args.partnerId}/updatePartnerVolumeEstimates`, payload, {
      headers: {
        authorization: getToken(),
      },
    });

    return this.fetchPartner(args.partnerId);
  }

  public async upsertReturnServiceMethod(partnerId: string, payload: Partial<ReturnServiceMethod>): Promise<Partner> {
    const result = await GatewayAxiosClient.put(`/partners/${partnerId}/returnServiceMethod`, payload, {
      headers: {
        authorization: getToken(),
      },
    });

    return result.data;
  }

  public async updatePartnerConfig(partner: Partner): Promise<Partner> {
    return (
      await GatewayAxiosClient.put(`/partner-management-proxy/api/v1/partners/${partner.partnerId}`, partner, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
  }

  public async updateReportingConfig(args: UpdateReportingConfigParams): Promise<PartnerReportingConfig> {
    return (
      await GatewayAxiosClient.post(
        `/partner/${args.partnerId}/createReportingConfig`,
        {
          timeZoneId: args.reportingZone,
          startingDayOfWeek: args.startDayOfWeek,
          orderToShipSlaDays: args.orderToShipSlaDays,
          orderToDeliverSlaDays: args.orderToDeliverSlaDays,
        },
        {
          headers: {
            authorization: getToken(),
          },
        },
      )
    ).data.reportingConfigurationInfo;
  }

  public async fetchPartner(partnerId: string): Promise<Partner> {
    const partner = (
      await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/${partnerId}`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
    const result: Partner = {
      ...partner,
      reportingConfig: partner.reportingConfigurationInfo,
      volumeEstimates: {
        weeklyVolumeEstimate: {
          averageProductPageVolume: partner.pageViewEstimates?.weekly?.average,
          peakProductPageVolume: partner.pageViewEstimates?.weekly?.peak,
        },
        dailyVolumeEstimate: {
          averageProductPageVolume: partner.pageViewEstimates?.daily?.average,
          peakProductPageVolume: partner.pageViewEstimates?.daily?.peak,
        },
        hourlyVolumeEstimate: {
          averageProductPageVolume: partner.pageViewEstimates?.hourly?.average,
          peakProductPageVolume: partner.pageViewEstimates?.hourly?.peak,
        },
      },
    };
    return result;
  }
  public async verifyEmailConfirmationToken(token: string): Promise<void> {
    return mockPartnerService.verifyEmailConfirmationToken(token);
  }
  public async updatePartnerOptIns(optIns: OptIns, partnerId: string): Promise<Partner> {
    return (
      await GatewayAxiosClient.put(
        `/partners/${partnerId}/updateOptIns`,
        { optIns, partnerId },
        {
          headers: {
            authorization: getToken(),
          },
        },
      )
    ).data;
  }
  public async updatePartnerPasswordSettings(partnerId: string, payload: PasswordConfigurationInfo) {
    const res = await GatewayAxiosClient.put(`/partners/${partnerId}/passwordConfigurationInfo`, payload, {
      headers: {
        authorization: getToken(),
      },
    });
    return res.data;
  }
  public async updatePartner(partner: Partner) {
    const currentPartner = (
      await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/${partner.partnerId}`, {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      })
    ).data;
    const updatedPartner = {
      ...currentPartner,
      ...partner,
    };
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/${partner.partnerId}`,
      updatedPartner,
      {
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      },
    );
    return res.data;
  }
  public async fetchV1AssetHistory(partnerId: string, params: any) {
    return (
      await GatewayAxiosClient.get(`/partner-configuration/configuration-audit/${partnerId}`, {
        params,
      })
    ).data;
  }
  public async createPartnerPackagingV2(packaging: PartnerPackagingV2): Promise<PartnerPackagingV2> {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/packaging`, packaging);
    return res.data;
  }
  public async updatePartnerPackagingV2(packaging: PartnerPackagingV2): Promise<PartnerPackagingV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/packaging/${packaging.packagingId}`,
      packaging,
    );
    return res.data;
  }
  public async fetchPartnerPackagingV2(): Promise<PartnerPackagingV2[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/packaging`);
    return res.data;
  }
  public async createPackagingSet(packagingSet: PackagingSetV2): Promise<PackagingSetV2> {
    const res = await GatewayAxiosClient.post(`/partner-management-proxy/api/v1/partners/packagingSets`, packagingSet);
    return res.data;
  }
  public async updatePackagingSet(packagingSetId: string, packagingSet: PackagingSetV2): Promise<PackagingSetV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}`,
      packagingSet,
    );
    return res.data;
  }
  public async fetchPackagingSets(): Promise<PackagingSetV2[]> {
    const res = await GatewayAxiosClient.get(`/partner-management-proxy/api/v1/partners/packagingSets`);
    return res.data;
  }
  public async addPackagingToSet(packagingSetId: string, packagingId: string): Promise<PackagingSetV2> {
    const res = await GatewayAxiosClient.put(
      `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}/packagings/${packagingId}`,
    );
    return res.data;
  }
  public async deletePackagingFromSet(packagingSetId: string, packagingId: string): Promise<PackagingSetV2> {
    const res = await GatewayAxiosClient.delete(
      `/partner-management-proxy/api/v1/partners/packagingSets/${packagingSetId}/packagings/${packagingId}`,
    );
    return res.data;
  }
}
